import React, { useState, useEffect, useCallback } from 'react';
import pb from './pocketbaseClient';
import { AdminApp } from './components/layout/AdminApp';
import { LoginPage } from './components/pages/LoginPage';
import { StandaloneApplicantForm } from './components/forms/StandaloneApplicantForm';
import { PublicFragebogenPage } from './components/pages/PublicFragebogenPage';
import { PublicAssessmentPage } from './components/pages/PublicAssessmentPage';
import { Router } from './components/Router';
import { RouteValidator } from './components/RouteValidator';
import { initializeCSS } from './utils/cssInjection';
import './input.css';

// Absolute session lifetime: users are logged out 12h after login, regardless
// of the PocketBase token duration. The start time is persisted so the cap
// survives reloads and is measured from the actual login, not from page load.
const SESSION_MAX_AGE_MS = 12 * 60 * 60 * 1000; // 12 hours
const SESSION_START_KEY = 'authSessionStart';

// Initialize CSS injection
initializeCSS();

// --- Router: Choose between Admin App and Standalone Form ---
export default function App() {
  const [currentPage, setCurrentPage] = useState({ page: 'dashboard', id: null });
  const [theme] = useState(() => {
    try {
      const saved = localStorage.getItem('theme');
      if (saved === 'light' || saved === 'dark') return saved;
      if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark';
    } catch {}
    return 'light';
  });
  const [user, setUser] = useState(() => {
    // Check if user is already authenticated
    if (pb.authStore.isValid) {
      return pb.authStore.record;
    }
    return null;
  });

  // Sync theme with localStorage
  useEffect(() => {
    localStorage.setItem('theme', theme);
    // Apply dark mode to html element for global CSS
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
      document.body.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
      document.body.classList.remove('dark');
    }
  }, [theme]);

  // Initial theme setup
  useEffect(() => {
    // Apply theme immediately on mount
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
      document.body.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
      document.body.classList.remove('dark');
    }
  }, [theme]);

  // Handle user login
  const handleLogin = (userRecord) => {
    try { localStorage.setItem(SESSION_START_KEY, String(Date.now())); } catch {}
    setUser(userRecord);
    setCurrentPage({ page: 'dashboard', id: null });
    // Theme preference is already preserved in localStorage, so no need to change it
  };

  // Handle user logout. `reason === 'expired'` surfaces a notice on the login page.
  const handleLogout = useCallback((reason) => {
    try {
      localStorage.removeItem(SESSION_START_KEY);
      if (reason === 'expired') sessionStorage.setItem('logoutReason', 'expired');
    } catch {}
    pb.authStore.clear();
    setUser(null);
    setCurrentPage({ page: 'login', id: null });
  }, []);

  // On mount: verify the stored token with the server and enforce the 12h cap.
  useEffect(() => {
    let cancelled = false;
    const validateSession = async () => {
      if (!pb.authStore.isValid) { setUser(null); return; }

      // Absolute 12h cap, measured from the persisted login time.
      let start = Number(localStorage.getItem(SESSION_START_KEY));
      if (!Number.isFinite(start) || start <= 0) {
        start = Date.now();
        try { localStorage.setItem(SESSION_START_KEY, String(start)); } catch {}
      }
      if (Date.now() - start >= SESSION_MAX_AGE_MS) { handleLogout('expired'); return; }

      // Confirm the token is still accepted server-side (catches expiry /
      // invalidation). Only log out on an auth rejection — keep the session on
      // transient network errors so a flaky connection doesn't sign users out.
      try {
        await pb.collection('users').authRefresh();
        if (!cancelled) setUser(pb.authStore.record);
      } catch (err) {
        if (cancelled) return;
        if (err?.status === 401 || err?.status === 403) handleLogout('expired');
        else setUser(pb.authStore.record);
      }
    };
    validateSession();
    return () => { cancelled = true; };
  }, [handleLogout]);

  // While the app is open, log out the moment the 12h lifetime elapses.
  useEffect(() => {
    if (!user) return;
    const start = Number(localStorage.getItem(SESSION_START_KEY)) || Date.now();
    const checkExpiry = () => {
      if (Date.now() - start >= SESSION_MAX_AGE_MS) handleLogout('expired');
    };
    checkExpiry();
    const intervalId = setInterval(checkExpiry, 60 * 1000); // re-check every minute
    return () => clearInterval(intervalId);
  }, [user, handleLogout]);

  // Early check for public routes - before any state initialization
  const urlParams = new URLSearchParams(window.location.search);
  const hasQuestionnaireId = Boolean(urlParams.get('id'));
  const isLegacyStandaloneLink = hasQuestionnaireId && (
    !urlParams.get('page') || /^\d+$/.test(urlParams.get('page') || '')
  );
  const isStandaloneApplicantForm = urlParams.get('page') === 'fragebogen' || 
                                   window.location.hostname === 'fragebogen.karrieresichern.de' ||
                                   isLegacyStandaloneLink;
  
  // Check if it's a public apply route FIRST (before RouteValidator)
  const applyRoutePattern = /^\/apply\/([^/]+)\/?$/;
  const applyMatch = window.location.pathname.match(applyRoutePattern);
  
  // If it's an apply route, render the public fragebogen page
  if (applyMatch) {
    const [, campaignId] = applyMatch;
    return <PublicFragebogenPage campaignId={campaignId} />;
  }
  
  // Check if it's a public landing page route - these should be accessible without login
  const publicLandingPagePattern = /^\/([^/]+)\/([^/]+)(?:\/([^/]+))?\/?$/;
  const publicLandingPageMatch = window.location.pathname.match(publicLandingPagePattern);
  
  // If it's a public landing page route, let Router handle it (no authentication required)
  if (publicLandingPageMatch) {
    return <Router />;
  }
  
  // Check if it's an edit route - these need authentication but should be handled by Router
  const editRoutePattern = /^\/([^/]+)\/([^/]+)(?:\/([^/]+))?\/edit\/?$/;
  const editMatch = window.location.pathname.match(editRoutePattern);
  
  // If it's an edit route, let Router handle it (it will check authentication)
  if (editMatch) {
    return <Router />;
  }
  
  // Check if it's the main karrieresichern.de domain - use RouteValidator for public routes
  if (window.location.hostname === 'karrieresichern.de') {
    return (
      <RouteValidator 
        pathname={window.location.pathname} 
        hostname={window.location.hostname}
      >
        <Router>
          {/* Router will handle public routes, but RouteValidator ensures they exist */}
        </Router>
      </RouteValidator>
    );
  }
  
  // Fragebogen 2 carries its own slug and has to be handled before the
  // standalone form, whose hostname check alone matches every link on
  // fragebogen.karrieresichern.de and would swallow this one.
  if (urlParams.get('page') === 'fragebogen2') {
    return (
      <PublicAssessmentPage
        assessmentId={urlParams.get('id')}
        token={urlParams.get('t')}
      />
    );
  }

  // If it's an applicant form, render completely independent component
  if (isStandaloneApplicantForm) {
    const questionnaireId = urlParams.get('id');
    // If no questionnaire ID is provided, show "no questionnaire found" message
    if (!questionnaireId) {
      return (
        <div className="bg-white dark:bg-slate-900 min-h-screen flex items-center justify-center">
          <div className="text-center p-8">
            <div className="font-bold text-red-600 mb-2">Kein Fragebogen angegeben.</div>
            <div className="text-sm text-slate-600 dark:text-slate-400">
              Bitte geben Sie eine gültige Fragebogen-ID an.
            </div>
          </div>
        </div>
      );
    }
    return <StandaloneApplicantForm questionnaireId={questionnaireId} />;
  }

  // Check if user is authenticated for admin routes
  if (!user && currentPage.page !== 'login') {
    return <LoginPage onLogin={handleLogin} />;
  }

  // If user is not authenticated and trying to access admin routes, redirect to login
  if (!user) {
    return <LoginPage onLogin={handleLogin} />;
  }

  // Otherwise render the admin app wrapped in Router
  return (
    <Router>
      <AdminApp user={user} handleLogout={handleLogout} />
    </Router>
  );
}



